import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { IPublicUser } from '@sravni/types/lib/auth';
import { pick } from 'lodash';

import type { GatewayRanking } from 'commonTypes/api/gatewayRanking';
import { AMOUNTS } from 'constants/amounts';
import { CREDIT_PURPOSES, PERIODS, CAR_AVAILABILITY, PROPERTY_TYPE_AVAILABILITY } from 'constants/dictionaries';
import type { QUERY_PARAMS } from 'constants/queryNames';

import { FormStepId } from 'shared/config/formStepId';
import type {
  TCustomerData,
  TCustomerRestoredWLData,
  TCustomerUpdateData,
  TLoansRestoredData,
} from 'shared/types/TCustomerService';

import type {
  TAdditionalDataFormFields,
  TCreditFormDictionaries,
  TLandingFormFields,
  TPassportFormFields,
  TWorkplaceFormFields,
} from 'entities/creditForm';
import {
  AdditionalInfoFormValidationSchema,
  ContactsFormValidationSchema,
  CreditPurposeFormValidationSchema,
  PassportFormValidationSchema,
  WorkplaceFormValidationSchema,
} from 'entities/creditForm';
import { mapDictionary } from 'entities/creditForm/lib/mapDictionary';
import { mapCustomerToCreditForm } from 'entities/creditForm/lib/mappersToCreditForm/mapCustomerToCreditForm';
import {
  mapLoansRestoreDataToCreditForm,
  mapQueryParamsToCreditForm,
} from 'src/client/entities/creditForm/lib/mappersToCreditForm';

import type {
  TContactsFormFields,
  TCreditFormEntityReducer,
  TCreditFormIncomingDictionaries,
  TCreditPurposeFormFields,
} from '../types';

import { formQueries } from './creditForm.query';

// Выбираемые значения должны быть null, чтобы при стирании они менялись на undefined и становились dirty
export const initialState: TCreditFormEntityReducer = {
  landingData: {
    // изначально должно быть undefined иначе каждый раз при появлении поля будет проставляться фокус
    period: undefined,
    purpose: undefined,
    amount: undefined,
  },
  creditPurposeForm: {
    data: null,
    defaults: {
      amount: String(AMOUNTS.defaultFormValue),
      period: PERIODS.find((p) => p.value === 'P3Y'),
      purpose: CREDIT_PURPOSES[0],
      autoBrand: null,
      autoModel: null,
      autoYear: null,
      initialFee: '',
    },
    isActive: true,
    isFullFilled: false,
    isOpenedInPopup: false,
  },
  contactsForm: {
    data: null,
    defaults: {
      email: '',
      mobilePhone: '',
      userAgreement: true,
      advertisingAgreement: true,
    },
    isActive: true,
    isFullFilled: false,
    isOpenedInPopup: false,
  },
  passportForm: {
    data: null,
    defaults: {
      fullName: null,
      gender: 'Unknown',
      birthDate: '',
      birthPlace: '',
      passportNumber: '',
      passportIssueDate: '',
      passportIssuedBy: '',
      unitCode: '',
      registrationAddress: null,
      registrationLocationRoute: '',
      registrationAddressFlat: '',
      registrationDate: '',
      residenceAddress: null,
      residenceLocationRoute: '',
      residenceAddressFlat: '',
      residenceAddressIsRegistration: true,
      region: null,
      regionLocationRoute: '',
      regionLocation: null,
    },
    isActive: true,
    isFullFilled: false,
    isOpenedInPopup: false,
  },
  workplaceForm: {
    data: null,
    defaults: {
      employmentType: null,
      unemploymentReason: null,
      anotherUnemploymentReason: '',
      monthlySalary: '',
      areaOfEmployment: null,
      okved: '',
      employerType: null,
      workPhone: '',
      jobTitle: null,
      employerInn: '',
      lastWorkExperienceStartMonth: '',
      workAddress: null,
      workLocationRoute: '',
      workOrganizationName: '',
      isWorkOrganizationManuallyFilled: false,
    },
    isActive: true,
    isFullFilled: false,
    isOpenedInPopup: false,
  },
  additionalDataForm: {
    data: null,
    defaults: {
      education: null,
      maritalStatus: null,
      numberOfDependents: null,
      additionalPhoneOwner: null,
      additionalPhoneOwnerFullName: null,
      carAvailability: CAR_AVAILABILITY.find((p) => p.value === 'No') || null,
      carAvailabilityToggleEnabled: false,
      propertyTypeAvailability: PROPERTY_TYPE_AVAILABILITY.find((p) => p.value === 'Nothing') || null,
      propertyTypeAvailabilityToggleEnabled: false,
      additionalPhone: '',
      snils: '',
    },
    isActive: true,
    isFullFilled: false,
    isOpenedInPopup: false,
  },
  isFilledByEsiaStatus: false,
  isAutoValidationInProgress: false,
  dictionaries: {
    purposes: [],
    autoBrands: [],
    autoModels: {},
    autoYears: {},
    employmentTypes: [],
    unemploymentReasons: [],
    incomeConfirmations: [],
    seniorityTypes: [],
    jobTypes: [],
    areasOfEmployment: [],
    employerTypes: [],
    educations: [],
    maritalStatuses: [],
    numberOfDependentsTypes: [],
    additionalPhoneOwners: [],
    carAvailabilityTypes: [],
    propertyTypeAvailabilityTypes: [],
    periods: [],
    jobPositions: [],
  },
  blockFF: undefined,
};

export const creditFormSlice = createSlice({
  name: 'creditForm',
  initialState,
  reducers: {
    setFormBlockFF: (state, { payload }: PayloadAction<GatewayRanking.RankingFeatureFlagsConfig | undefined>) => {
      if (payload) {
        const { preSelectedPeriod, preSelectedAmount, preSelectedPurpose } = payload;
        state.blockFF = {
          ...state.blockFF,
          ...payload,
        };
        state.creditPurposeForm.data = {
          ...state.creditPurposeForm.data,
          purpose: state.creditPurposeForm.data?.purpose || CREDIT_PURPOSES.find((p) => p.value === preSelectedPurpose),
          period: state.creditPurposeForm.data?.period || PERIODS.find((p) => p.value === preSelectedPeriod),
          amount: state.creditPurposeForm.data?.amount || (preSelectedAmount ? String(preSelectedAmount) : ''),
        } as TCreditPurposeFormFields;
      }
    },
    setCreditFormDictionaries: (state, { payload }: PayloadAction<Partial<TCreditFormIncomingDictionaries>>) => {
      const payLoadDictionaries = mapDictionary(payload);
      state.dictionaries = {
        ...state.dictionaries,
        ...payLoadDictionaries,
      };
    },
    setCreditPurposeInfoDefault: (state, { payload }: PayloadAction<Partial<TCreditPurposeFormFields>>) => {
      state.creditPurposeForm.defaults = {
        ...initialState.creditPurposeForm.defaults,
        ...payload,
      };
      state.creditPurposeForm.isFullFilled = false;
    },
    setCreditPurposeInfo: (
      state,
      { payload }: PayloadAction<{ data: Partial<TCreditPurposeFormFields>; isFullFilled?: boolean }>,
    ) => {
      state.creditPurposeForm.data = {
        ...state.creditPurposeForm.data,
        ...payload.data,
      } as TCreditPurposeFormFields;

      state.creditPurposeForm.isFullFilled =
        payload.isFullFilled !== undefined
          ? payload.isFullFilled
          : CreditPurposeFormValidationSchema().isValidSync(payload.data);
    },
    setCreditParams: (
      state,
      { payload }: PayloadAction<Partial<Pick<TCreditPurposeFormFields, 'purpose' | 'amount' | 'period'>>>,
    ) => {
      state.creditPurposeForm.data = {
        ...state.creditPurposeForm.data,
        ...payload,
      } as TCreditPurposeFormFields;
    },
    setContactsInfoDefault: (state, { payload }: PayloadAction<Partial<TContactsFormFields>>) => {
      state.contactsForm.defaults = {
        ...initialState.contactsForm.defaults,
        ...payload,
      };
      state.contactsForm.isFullFilled = false;
    },
    setContactsInfo: (
      state,
      { payload }: PayloadAction<{ data: Partial<TContactsFormFields>; isFullFilled?: boolean }>,
    ) => {
      state.contactsForm.data = {
        ...state.contactsForm.data,
        ...payload.data,
      } as TContactsFormFields;

      state.contactsForm.isFullFilled =
        payload.isFullFilled !== undefined
          ? payload.isFullFilled
          : ContactsFormValidationSchema(true).isValidSync(payload.data);
    },
    setLandingInfo: (state, { payload }: PayloadAction<{ data: Partial<TLandingFormFields> }>) => {
      const contactsFormPayload = pick(payload.data || {}, Object.keys(state.contactsForm.defaults));
      const passportFormPayload = pick(payload.data || {}, Object.keys(state.passportForm.defaults));
      const creditPurposeFormPayload = pick(payload.data || {}, Object.keys(state.creditPurposeForm.defaults));

      state.contactsForm.data = {
        ...state.contactsForm.data,
        ...contactsFormPayload,
      } as TContactsFormFields;

      state.creditPurposeForm.data = {
        ...state.creditPurposeForm.data,
        ...creditPurposeFormPayload,
      } as TCreditPurposeFormFields;

      state.passportForm.data = {
        ...state.passportForm.data,
        ...passportFormPayload,
      } as TPassportFormFields;
    },
    setContactsInfoFromUser: (state, { payload }: PayloadAction<IPublicUser>) => {
      const { email, mobilePhone } = state.contactsForm.data || {};
      const { fullName } = state.passportForm.data || {};
      const fio = fullName || { value: '', label: '' };
      if (payload.last_name && payload.first_name) {
        const value = `${payload.last_name} ${payload.first_name}`;
        fio.value = value;
        fio.label = value;
        fio.data = {
          surname: payload.last_name,
          name: payload.first_name,
          patronymic: null,
          gender: 'UNKNOWN',
          source: null,
          qc: '0',
        };
      }

      state.contactsForm.data = {
        ...state.contactsForm.data,
        advertisingAgreement: true,
        userAgreement: true,
        email: payload.email || email || '',
        mobilePhone: payload.phone_number || mobilePhone || '',
      };

      state.passportForm.data = {
        ...state.passportForm.data,
        fullName: fio,
        gender: 'Unknown',
      } as TPassportFormFields;
    },
    setPassportInfoDefault: (state, { payload }: PayloadAction<Partial<TPassportFormFields>>) => {
      state.passportForm.defaults = {
        ...initialState.passportForm.defaults,
        ...payload,
      };
      state.passportForm.isFullFilled = false;
    },
    setFormIsOpenedInPopupByStepId: (
      state,
      { payload }: PayloadAction<{ stepId: ValuesOf<typeof FormStepId>; isOpened: boolean }>,
    ) => {
      const { stepId, isOpened } = payload;
      switch (stepId) {
        case FormStepId.CreditPurposeInfo:
          state.creditPurposeForm.isOpenedInPopup = isOpened;
          break;
        case FormStepId.ContactsInfo:
          state.contactsForm.isOpenedInPopup = isOpened;
          break;
        case FormStepId.PassportInfo:
          state.passportForm.isOpenedInPopup = isOpened;
          break;
        case FormStepId.WorkplaceInfo:
          state.workplaceForm.isOpenedInPopup = isOpened;
          break;
        case FormStepId.AdditionalInfo:
          state.additionalDataForm.isOpenedInPopup = isOpened;
          break;
        default:
          break;
      }
    },
    setPassportInfo: (
      state,
      { payload }: PayloadAction<{ data: Partial<TPassportFormFields>; isFullFilled?: boolean }>,
    ) => {
      state.passportForm.data = {
        ...state.passportForm.data,
        ...payload.data,
      } as TPassportFormFields;

      state.passportForm.isFullFilled =
        payload.isFullFilled !== undefined
          ? payload.isFullFilled
          : PassportFormValidationSchema().isValidSync(payload.data);
    },
    setWorkplaceInfo: (
      state,
      { payload }: PayloadAction<{ data: Partial<TWorkplaceFormFields>; isFullFilled?: boolean }>,
    ) => {
      state.workplaceForm.data = {
        ...state.workplaceForm.data,
        ...payload.data,
      } as TWorkplaceFormFields;

      state.workplaceForm.isFullFilled =
        payload.isFullFilled !== undefined
          ? payload.isFullFilled
          : WorkplaceFormValidationSchema().isValidSync(payload.data);
    },
    setAdditionalInfo: (
      state,
      { payload }: PayloadAction<{ data: Partial<TAdditionalDataFormFields>; isFullFilled?: boolean }>,
    ) => {
      state.additionalDataForm.data = {
        ...state.additionalDataForm.data,
        ...payload.data,
      } as TAdditionalDataFormFields;

      state.additionalDataForm.isFullFilled =
        payload.isFullFilled !== undefined
          ? payload.isFullFilled
          : AdditionalInfoFormValidationSchema().isValidSync(payload.data);
    },
    setCreditFormByCustomerData: (state, { payload }: PayloadAction<TCustomerData>) => {
      const { creditPurposeData, contactsData, workplaceData, additionalData, passportData } = mapCustomerToCreditForm(
        state,
        payload,
      );
      state.creditPurposeForm.data = creditPurposeData;
      state.contactsForm.data = {
        ...contactsData,
        mobilePhone: contactsData.mobilePhone || state.contactsForm.data?.mobilePhone || '',
      };
      state.passportForm.data = passportData;
      state.workplaceForm.data = workplaceData;
      state.additionalDataForm.data = additionalData;
    },
    setCreditFormByRestoreData: (state, { payload }: PayloadAction<TLoansRestoredData['customer']>) => {
      const { creditPurposeData, contactsData, workplaceData, additionalData, passportData } =
        mapLoansRestoreDataToCreditForm(state, payload);
      state.creditPurposeForm.data = creditPurposeData;
      state.contactsForm.data = contactsData;
      state.passportForm.data = passportData;
      state.workplaceForm.data = workplaceData;
      state.additionalDataForm.data = additionalData;
    },
    updateCreditFormByQueryParams: (
      state,
      { payload }: PayloadAction<Partial<Record<ValuesOf<typeof QUERY_PARAMS>, string>>>,
    ) => {
      state.creditPurposeForm.data = {
        ...state.creditPurposeForm.data,
        ...mapQueryParamsToCreditForm(state, payload),
      } as TCreditPurposeFormFields;
    },
    setCreditFormLocationRoutes: (
      state,
      {
        payload,
      }: PayloadAction<{
        regionLocationRoute?: string;
        registrationLocationRoute?: string;
        residenceLocationRoute?: string;
        workLocationRoute?: string;
        additionalDataLocationRoute?: string;
      }>,
    ) => {
      const { regionLocationRoute, registrationLocationRoute, residenceLocationRoute, workLocationRoute } = payload;

      state.passportForm.data = {
        ...state.passportForm.data,
        registrationLocationRoute: registrationLocationRoute ?? state.passportForm.data?.registrationLocationRoute,
        residenceLocationRoute: residenceLocationRoute ?? state.passportForm.data?.residenceLocationRoute,
        regionLocationRoute: regionLocationRoute ?? state.passportForm.data?.regionLocationRoute,
      } as TPassportFormFields;

      state.workplaceForm.data = {
        ...state.workplaceForm.data,
        workLocationRoute: workLocationRoute ?? state.workplaceForm.data?.workLocationRoute,
      } as TWorkplaceFormFields;
    },
    setRegionLocation: (state, { payload }: PayloadAction<ILocationV2>) => {
      state.passportForm.data = {
        ...state.passportForm.data,
        regionLocation: payload,
      } as TPassportFormFields;
    },
    clearEsiaFlag: (state) => {
      state.isFilledByEsiaStatus = initialState.isFilledByEsiaStatus;
    },
    setAutoValidationInProgress: (state, { payload }: PayloadAction<boolean>) => {
      state.isAutoValidationInProgress = payload;
    },
    clearDictionary: (state, { payload }: PayloadAction<keyof TCreditFormDictionaries>) => {
      switch (payload) {
        case 'autoModels':
        case 'autoYears': {
          state.dictionaries[payload] = {};
          break;
        }
        default: {
          state.dictionaries[payload] = [];
        }
      }
    },
    setIsStepsFullFilled: (
      state,
      {
        payload,
      }: PayloadAction<{
        isCreditPurposeFullFilled?: boolean;
        isContactsFullFilled?: boolean;
        isPassportFullFilled?: boolean;
        isWorkplaceFullFilled?: boolean;
        isAdditionalDataFullFilled?: boolean;
      }>,
    ) => {
      state.creditPurposeForm.isFullFilled = payload.isCreditPurposeFullFilled || state.creditPurposeForm.isFullFilled;
      state.contactsForm.isFullFilled = payload.isContactsFullFilled || state.contactsForm.isFullFilled;
      state.passportForm.isFullFilled = payload.isPassportFullFilled || state.passportForm.isFullFilled;
      state.workplaceForm.isFullFilled = payload.isWorkplaceFullFilled || state.workplaceForm.isFullFilled;
      state.additionalDataForm.isFullFilled =
        payload.isAdditionalDataFullFilled || state.additionalDataForm.isFullFilled;
    },
    setIsStepFullFilledByStepId: (
      state,
      { payload }: PayloadAction<{ stepId: ValuesOf<typeof FormStepId>; isFullFilled: boolean }>,
    ) => {
      const { stepId, isFullFilled } = payload;

      switch (stepId) {
        case FormStepId.CreditPurposeInfo:
          state.creditPurposeForm.isFullFilled = isFullFilled;
          break;
        case FormStepId.ContactsInfo:
          state.contactsForm.isFullFilled = isFullFilled;
          break;
        case FormStepId.PassportInfo:
          state.passportForm.isFullFilled = isFullFilled;
          break;
        case FormStepId.WorkplaceInfo:
          state.workplaceForm.isFullFilled = isFullFilled;
          break;
        case FormStepId.AdditionalInfo:
          state.additionalDataForm.isFullFilled = isFullFilled;
          break;
        default:
          break;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      formQueries.endpoints.getAutoModels.matchFulfilled,
      (state, { payload }: PayloadAction<TCreditFormDictionaries['autoModels']>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          autoModels: {
            ...state.dictionaries.autoModels,
            ...payload,
          },
        },
      }),
    );

    builder.addMatcher(
      formQueries.endpoints.getAutoManufactureYears.matchFulfilled,
      (state, { payload }: PayloadAction<TCreditFormDictionaries['autoYears']>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          autoYears: {
            ...state.dictionaries.autoYears,
            ...payload,
          },
        },
      }),
    );

    builder.addMatcher(
      formQueries.endpoints.getAutoBrands.matchFulfilled,
      (state, { payload }: PayloadAction<TCreditFormDictionaries['autoBrands']>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          autoBrands: payload || state.dictionaries.autoBrands,
        },
      }),
    );

    builder.addMatcher(
      formQueries.endpoints.getUnitCodes.matchFulfilled,
      (state, { payload }: PayloadAction<Dictionaries.TSelectOption[]>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          unitCodes: payload,
        },
      }),
    );

    builder.addMatcher(
      formQueries.endpoints.getUserData.matchFulfilled,
      (state, { payload }: PayloadAction<TCustomerUpdateData>) => {
        const { workplaceData, additionalData, passportData, contactsData } = mapCustomerToCreditForm(state, payload);
        state.contactsForm.data = {
          ...state.contactsForm.data,
          email: contactsData.email,
        } as TContactsFormFields;
        state.passportForm.data = passportData;
        state.workplaceForm.data = workplaceData;
        state.additionalDataForm.data = additionalData;
      },
    );
    // обновляем данные только начиная с переданного шага (на котором выводится баннер ЕСИА), чтобы не перезаписывать уже введённые данные
    builder.addMatcher(
      formQueries.endpoints.getUserDataAfterEsia.matchFulfilled,
      (state, action: PayloadAction<TCustomerUpdateData, string, { arg: { originalArgs?: FormStepId } }>) => {
        const passedStepId = action.meta?.arg?.originalArgs;
        const { payload } = action;

        const { workplaceData, additionalData, passportData, creditPurposeData, contactsData } =
          mapCustomerToCreditForm(state, payload);

        const FORM_STEPS_ORDER = [
          FormStepId.CreditPurposeInfo,
          FormStepId.ContactsInfo,
          FormStepId.PassportInfo,
          FormStepId.WorkplaceInfo,
          FormStepId.AdditionalInfo,
        ] as const;

        const startIndex = passedStepId ? FORM_STEPS_ORDER.indexOf(passedStepId) : 0;
        const stepsToUpdate = FORM_STEPS_ORDER.slice(startIndex === -1 ? 0 : startIndex);

        stepsToUpdate.forEach((stepId) => {
          switch (stepId) {
            case FormStepId.CreditPurposeInfo: {
              state.creditPurposeForm.data = creditPurposeData;
              break;
            }
            case FormStepId.ContactsInfo: {
              state.contactsForm.data = {
                ...state.contactsForm.data,
                email: contactsData.email,
              } as TContactsFormFields;
              break;
            }
            case FormStepId.PassportInfo: {
              state.passportForm.data = passportData;
              break;
            }
            case FormStepId.WorkplaceInfo: {
              state.workplaceForm.data = workplaceData;
              break;
            }
            case FormStepId.AdditionalInfo: {
              state.additionalDataForm.data = additionalData;
              break;
            }
            default:
              break;
          }
        });
      },
    );
    builder.addMatcher(
      formQueries.endpoints.restoreWhiteLabelUserData.matchFulfilled,
      (state, { payload }: PayloadAction<TCustomerRestoredWLData>) => {
        const { data, routes } = payload;
        const { creditPurposeData, contactsData, workplaceData, additionalData, passportData } =
          mapCustomerToCreditForm(state, data);
        state.creditPurposeForm.data = creditPurposeData;
        state.contactsForm.data = contactsData;
        state.passportForm.data = passportData;
        state.workplaceForm.data = workplaceData;
        state.additionalDataForm.data = additionalData;

        if (!routes) {
          return;
        }

        const { regionLocationRoute, registrationLocationRoute, residenceLocationRoute, workLocationRoute } = routes;

        state.passportForm.data = {
          ...state.passportForm.data,
          registrationLocationRoute: registrationLocationRoute ?? state.passportForm.data?.registrationLocationRoute,
          residenceLocationRoute: residenceLocationRoute ?? state.passportForm.data?.residenceLocationRoute,
          regionLocationRoute: regionLocationRoute ?? state.passportForm.data?.regionLocationRoute,
        } as TPassportFormFields;

        state.workplaceForm.data = {
          ...state.workplaceForm.data,
          workLocationRoute: workLocationRoute ?? state.workplaceForm.data?.workLocationRoute,
        } as TWorkplaceFormFields;
      },
    );
    builder.addMatcher(
      formQueries.endpoints.restoreUserData.matchFulfilled,
      (state, { payload }: PayloadAction<TLoansRestoredData['customer']>) => {
        const { workplaceData, additionalData, passportData, contactsData, creditPurposeData } =
          mapLoansRestoreDataToCreditForm(state, payload);
        state.creditPurposeForm.data = creditPurposeData;
        state.contactsForm.data = contactsData;
        state.passportForm.data = passportData;
        state.workplaceForm.data = workplaceData;
        state.additionalDataForm.data = additionalData;
      },
    );

    builder.addMatcher(
      formQueries.endpoints.getJobPositions.matchFulfilled,
      (state, { payload }: PayloadAction<TCreditFormDictionaries['jobPositions']>) => ({
        ...state,
        dictionaries: {
          ...state.dictionaries,
          jobPositions: payload || state.dictionaries.jobPositions,
        },
      }),
    );
  },
});

export const {
  setCreditFormDictionaries,
  setCreditPurposeInfo,
  setContactsInfo,
  setContactsInfoFromUser,
  setPassportInfo,
  setWorkplaceInfo,
  setAdditionalInfo,
  clearEsiaFlag,
  setCreditFormByCustomerData,
  setCreditFormByRestoreData,
  setIsStepsFullFilled,
  setCreditFormLocationRoutes,
  setRegionLocation,
  updateCreditFormByQueryParams,
  setCreditParams,
  setFormIsOpenedInPopupByStepId,
  setLandingInfo,
  clearDictionary,
  setFormBlockFF,
  setAutoValidationInProgress,
  setIsStepFullFilledByStepId,
} = creditFormSlice.actions;
